import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

interface Notification {
  id: string;
  company_id: string;
  branch_id?: string;
  user_id?: string;
  type: string;
  title: string;
  message: string;
  data?: any;
  is_read: boolean;
  created_at: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
}

export function useNotifications() {
  const { user, selectedCompany } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  // Load notifications
  const loadNotifications = async () => {
    if (!user) return;

    try {
      let query = supabase
        .from('notifications')
        .select('*');

      if (selectedCompany?.id) {
        // Notificaciones de la compañía o dirigidas al usuario
        query = query.or(`company_id.eq.${selectedCompany.id},user_id.eq.${user.id}`);
      } else {
        // Fallback: solo las del usuario
        query = query.eq('user_id', user.id);
      }

      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(50);


      if (error) throw error;

      const typedNotifications = (data || []).map(n => ({
        ...n,
        priority: n.priority as 'low' | 'normal' | 'high' | 'urgent'
      }));
      console.log('📬 Notificaciones cargadas:', typedNotifications.length, typedNotifications);
      setNotifications(typedNotifications);
      setUnreadCount(typedNotifications.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('Error loading notifications:', error);
      toast.error("Error al cargar las notificaciones");
    } finally {
      setLoading(false);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      if (error) throw error;

      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, is_read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error("Error al marcar notificación como leída");
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!selectedCompany?.id) return;

    try {
      // Marcar como leídas tanto de la compañía como personales
      const baseUpdate = supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('is_read', false);

      const { error } = selectedCompany?.id
        ? await baseUpdate.or(`company_id.eq.${selectedCompany.id},user_id.eq.${user?.id}`)
        : await baseUpdate.eq('user_id', user!.id);

      if (error) throw error;

      setNotifications(prev => 
        prev.map(n => ({ ...n, is_read: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error("Error al marcar todas las notificaciones como leídas");
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) throw error;

      const deletedNotification = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      if (deletedNotification && !deletedNotification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error("Error al eliminar la notificación");
    }
  };

  // Set up real-time subscription
  useEffect(() => {
    if (!user) return;

    loadNotifications();

    const channel = supabase.channel('notifications-changes');

    // INSERT events
    if (selectedCompany?.id) {
      channel.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `company_id=eq.${selectedCompany.id}`
        },
        (payload) => {
          const newNotification = {
            ...payload.new,
            priority: payload.new.priority as 'low' | 'normal' | 'high' | 'urgent'
          } as Notification;

          console.log('🔔 Nueva notificación (company):', newNotification);

          setNotifications(prev => {
            const exists = prev.some(n => n.id === newNotification.id);
            return exists ? prev : [newNotification, ...prev];
          });
          setUnreadCount(prev => prev + (newNotification.is_read ? 0 : 1));

          const shouldToast = ['mention','assigned','comment','message','task_assigned','task_completed','task_comment'].includes(newNotification.type)
            || newNotification.priority === 'high'
            || newNotification.priority === 'urgent';

          if (shouldToast) {
            toast(newNotification.title, {
              description: newNotification.message,
              duration: newNotification.priority === 'urgent' ? 10000 : 5000,
            });
          }
        }
      );
    }

    // Personal notifications (user-specific)
    channel.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${user.id}`
      },
      (payload) => {
        const newNotification = {
          ...payload.new,
          priority: payload.new.priority as 'low' | 'normal' | 'high' | 'urgent'
        } as Notification;

        console.log('🔔 Nueva notificación (user):', newNotification);

        setNotifications(prev => {
          const exists = prev.some(n => n.id === newNotification.id);
          return exists ? prev : [newNotification, ...prev];
        });
        setUnreadCount(prev => prev + (newNotification.is_read ? 0 : 1));

        const shouldToast = ['mention','assigned','comment','message','task_assigned','task_completed','task_comment'].includes(newNotification.type)
          || newNotification.priority === 'high'
          || newNotification.priority === 'urgent';

        if (shouldToast) {
          toast(newNotification.title, {
            description: newNotification.message,
            duration: newNotification.priority === 'urgent' ? 10000 : 5000,
          });
        }
      }
    );

    // UPDATE events
    if (selectedCompany?.id) {
      channel.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `company_id=eq.${selectedCompany.id}`
        },
        (payload) => {
          const updatedNotification = {
            ...payload.new,
            priority: payload.new.priority as 'low' | 'normal' | 'high' | 'urgent'
          } as Notification;
          setNotifications(prev =>
            prev.map(n => n.id === updatedNotification.id ? updatedNotification : n)
          );
        }
      );
    }

    channel.on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${user.id}`
      },
      (payload) => {
        const updatedNotification = {
          ...payload.new,
          priority: payload.new.priority as 'low' | 'normal' | 'high' | 'urgent'
        } as Notification;
        setNotifications(prev =>
          prev.map(n => n.id === updatedNotification.id ? updatedNotification : n)
        );
      }
    );

    channel.subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, selectedCompany?.id]);

  return {
    notifications,
    loading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refresh: loadNotifications
  };
}